import { ConsolidationBuilder } from "./Consolidation";
import THREE from "three";
import { DEFAULT_CONSOLIDATION_POLYCOUNT_LIMIT } from "../../globals";
import { BVH } from "../BVHBuilder";
import { applyInstancingToRange } from "./ConsolidationCommon";

/** @import { MaterialManager } from "../../render/MaterialManager" */
/** @import { Consolidation } from "./Consolidation" */
/** @import { ConsolidationMap } from "./Consolidation" */
/** @import { NodeConsolidation } from "./NodeConsolidation"; */
/** @import { NodeArray } from "../BVHBuilder" */

/**
 * This file contains code to create a Consolidation (see Consolidation.js) from all fraqments of a FragmentList.
 * Rendering the consolidation instead of the individual fragments can improve rendering performance
 * for models containing a large number of small objects.
 */

/**
 * Function to check if the consolidation job has been canceled. Throws if so.
 *
 * @callback fnStopIfCanceled
 * @throws {Error} If the consolidation job has been canceled
 */

/**
 * Context information for the consolidation job
 *
 * @typedef {Object} ConsolidationJobContext
 * @property {fnStopIfCanceled} stopIfCanceled            - Checks if the consolidation job has been canceled. Throws if so.
 * @property {ConsolidationMap|null} consolidationMap     - If available, the intermediate results can be reused from a previous
 *                                                          consolidation to accelerate preprocessing. Note that a ConsolidationMap
 *                                                          can only be reused if the FragmentList and consolidation parameters are exactly the same.
 * @property {number} byteLimit                           - The memory limit for consolidation
 * @property {boolean} forceConsolidationMapRecomputation - If true, the consolidation map will be recomputed
 * @property {Worker} worker                              - The worker that is used for consolidation
 * @property {number} id                                  - Consolidation Job identifier
 * @property {number} byteLimit                           - Merging geometries is the most efficient technique in terms
 *                                                          of rendering performance. But, it can strongly increase
 *                                                          the memory consumption, particularly because merged
 *                                                          geometry cannot be shared, i.e. multiple instances of
 *                                                          a single geometry must be replicated per instance for merging.
 *                                                          Therefore, the memory spent for merging is restricted.
 *                                                          A higher value may make rendering faster, but increases (also GPU) memory
 *                                                          workload.
 * @property {Object} bvhOptions                          - BVH computation options
 * @property {Object} consolidationBVH                    - The BVH computed during consolidation
 * @property {NodeArray} consolidationBVH.nodes           - The BVH nodes
 * @property {Int32Array} consolidationBVH.primitives     - The BVH primitives
 * @property {number} syncTimeSlice                       - Max miliseconds to spend on consolidation per iteration
 * @property {boolean} useDeferredConsolidation           - If true, consolidation will only compute the initial data. Consolidated buffers will be computed on demand.
 * @property {RenderModel} model                          - The model being consolidated
 */

export const CONSOLIDATION_STOP_MARKER = Object.freeze({
  BVH_SORTING: { name: 'Sorting fragments for BVH computation', progress: 10 },
  BVH: { name: 'Computing Consolidation BVH', progress: 30 },
  SORTING: { name: 'Sorting Fragments by Consolidation Costs', progress: 50 },
  MAP_CREATION: { name: 'Creating Consolidation Map', progress: 50 },
  INSTANCING: { name: 'Applying Instancing to Fragments', progress: 70 },
  FINAL_PROCESSING: { name: 'Finalizing Consolidation', progress: 95 }
});

const FRAGMENTS_PER_TIME_CHECK = 10_000;

let workerJobId = 0;

/**
 *  Creates a consolidated representation for a given list of fragment ids. Consolidation is only done for the
 *  first n elements of the fragIds array, where n is chosen in a way that we stop if a given memory cost limit is reached.
 *
 *  Consolidation is done here by merging fragment Geometries into larger vertex buffers. If multiple fragments share
 *  the same geometry, the geometry is replicated. Therefore, this step is only used for the smaller fragments
 *  with not too many instances.
 *
 *   @param {RenderModel}             model            - The model to consolidate
 *   @param {Int32Array[]}            fragIds          - Array of fragment ids to consolidate
 *   @param {Uint32Array|null}        fragIdToNodeIdx  - Optional: If available, the bvh node index for each fragment
 *   @param {ConsolidationJobContext} jobContext       - Context information for the consolidation job
 *
 *   @returns {Promise<Object>} Result object containing...
 *                      result.consolidation: Instance of Consolidation
 *                      result.fragIdCount:   Defines a range within fragIds:
 *                                            Only fragIds[0], ... , fragIds[result.fragIdCount-1] were consolidated.
 */
async function createConsolidationMap(model, fragIds, fragIdToNodeIdx, jobContext) {
  const fragList = model.getFragmentList();
  const polyCountLimit = jobContext.bvhOptions?.consolidation_polycount_limit ?? DEFAULT_CONSOLIDATION_POLYCOUNT_LIMIT;

  return new Promise((resolve) => {
    // reused in loop below
    var fragBox = new THREE.Box3();
    const geomList = model.getGeometryList();

    var mc = new ConsolidationBuilder();
    var i = 0;

    const addMoreGeoms = () => {
      jobContext.stopIfCanceled(CONSOLIDATION_STOP_MARKER.MAP_CREATION);

      const endTime = performance.now() + jobContext.syncTimeSlice;
      for (; i < fragIds.length; i++) {
        let fragId = fragIds[i];

        // stop if we reached our memory limit.
        if (mc.costs >= jobContext.byteLimit) {
          break;
        }

        // get world box
        fragList.getWorldBounds(fragId, fragBox);

        // add mesh to consolidation
        let materialId, geometry;
        geometry = fragList.getGeometry(fragId);
        const material = fragList.getMaterial(fragId);
        if (material.transparent) {
          break;
        }
        materialId = material.id;

        mc.addGeom(geometry, materialId, fragBox, fragId, fragIdToNodeIdx);

        // check if we need to yield because we are running out of time
        if (i % FRAGMENTS_PER_TIME_CHECK === 0) {
          const diffToInstancing = CONSOLIDATION_STOP_MARKER.INSTANCING.progress - CONSOLIDATION_STOP_MARKER.MAP_CREATION.progress;
          jobContext.signalProgress(CONSOLIDATION_STOP_MARKER.MAP_CREATION.progress + diffToInstancing * Math.max(i / fragIds.length, mc.costs / jobContext.byteLimit));

          if (performance.now() > endTime) {
            ++i;
            setTimeout(addMoreGeoms, 0);
            return;
          }
        }
      }

      // create ConsolidationMap
      resolve(mc.createConsolidationMap(fragIds, i, fragIdToNodeIdx));
    };

    addMoreGeoms();
  });
}

/**
 * Combines fragments with shared geometries into instanced meshes. Note that creating instanced meshes
 * only makes sense for fragments that share geometry and material. All other fragments will also be
 * added to the result, but the meshes will share original geometry and material.
 *
 * Requirement: fragIds must already be sorted in a way that meshes with identical geometry and material form
 *              a contiguous range.
 *
 * @param {RenderModel}             model      - Model to be consolidated
 * @param {MaterialManager}         materials  - Needed to create new materials for instanced shapes
 * @param {Consolidation} consolidation        - Consolidation object to which the instancing is applied
 * @param {ConsolidationJobContext} jobContext - Context information for the consolidation job
 * @param {Promise<Consolidation>} result - collects all output meshes
 */
async function applyInstancing(model, materials, consolidation, jobContext) {
  const MAX_INDICES_PER_RANGE = 50_000;
  var fragList = model.getFragmentList();

  // the first n=numConsolidated fragments in fragIds are consolidated already.
  // The remaining fragIds are now processed using hardware instancing.
  const fragIds = jobContext.consolidationMap.fragOrder;
  const startIndex = jobContext.consolidationMap.numConsolidated;
  const bvhNodeToInstancingRanges = jobContext.consolidationMap.bvhNodeToInstancingRanges = new Array();

  if (startIndex >= fragIds.length) {
    // range empty
    // This may happen if we could consolidate all fragments per mesh merging already, so
    // that instancing is not needed anymore.
    return;
  }

  // track ranges of equal geometry and material
  var rangeStart = startIndex;
  var lastGeomId = -1;
  var lastMatId = -1;
  let lastNodeIdx = -1;
  let indexCount = 0;
  const fragIdToNodeIdx = jobContext.consolidationMap.fragIdToNodeIdx;

  let { geomIds, materialIds } = getGeomAndMaterialIDs(model);


  consolidation.fragId2InstancingRange = new Array();
  const process = (rangeStart, rangeEnd) => {
    applyInstancingToRange(model, materials, fragIds, rangeStart, rangeEnd, consolidation);
  };

  let endTime = performance.now() + jobContext.syncTimeSlice;
  for (let i = startIndex; i < fragIds.length; i++) {
    var fragId = fragIds[i];
    var geomId = geomIds[fragId];
    var matId = materialIds[fragId];
    var nodeIdx = fragIdToNodeIdx ? fragIdToNodeIdx[fragId] : -1;

    // check if a new range starts here
    // If case of per tile consolidation, we allow pulling in more fragments from other nodes
    // to a certain degree to increase render batch sizes.
    if (geomId != lastGeomId || matId != lastMatId ||
    nodeIdx != lastNodeIdx && indexCount > MAX_INDICES_PER_RANGE) {

      // a new range starts at index i
      // => process previous range [rangeStart, ..., i-1]
      if (i != startIndex) {
        process(rangeStart, i);
      }

      // start new range
      rangeStart = i;
      lastGeomId = geomId;
      lastMatId = matId;
      lastNodeIdx = nodeIdx;
      indexCount = 0;
    }


    const geom = fragList.getGeometry(fragId);
    indexCount += geom.ib ? geom.ib.length : 0;

    if (i % FRAGMENTS_PER_TIME_CHECK === 0) {
      const diffToFinalProcessing = CONSOLIDATION_STOP_MARKER.FINAL_PROCESSING.progress - CONSOLIDATION_STOP_MARKER.INSTANCING.progress;
      jobContext.signalProgress(CONSOLIDATION_STOP_MARKER.INSTANCING.progress + diffToFinalProcessing * i / fragIds.length);

      if (performance.now() > endTime) {
        await new Promise((resolve) => setTimeout(resolve, 0));
        endTime = performance.now() + jobContext.syncTimeSlice;
        jobContext.stopIfCanceled(CONSOLIDATION_STOP_MARKER.INSTANCING);
      }
    }
  }

  // process final range
  process(rangeStart, fragIds.length);
}

/**
 * Returns an array that enumerates all fragIds in a way that...
 *
 *  1. They are ordered by increasing memory costs that it takes to consolidate them.
 *  2. FragIds with equal geometry and material form a contiguous range.
 *  3. If available by bvh node index, so that fragments in the same node are grouped together.
 *
 *  @param {RenderModel} model                  - The model to consolidate
 *  @param {Int32Array}  [sortedFragIds]        - Optional: If available, the array will be reused to store the result
 *  @param {Uint32Array} [fragIdToNodeIdx]      - Optional: If available, sorting will take into account the bvh node index
 *  @param {ConsolidationJobContext} jobContext - Context information for the consolidation job
 *  @returns {Promise<Int32Array>} ordered list of fragment ids, wrapped in a promise
 */
async function sortByConsolidationCosts(model, sortedFragIds, fragIdToNodeIdx, jobContext) {
  const fragList = model.getFragmentList();
  const geomList = model.getGeometryList();

  // a single missing geometry shouldn't make the whole consolidation fail.
  // therefore, we exclude any null-geometry fragemnts.
  var validFrags = 0;

  // create fragId array [0,1,2,...]
  var fragCount = fragList.getCount();
  var fragIds = sortedFragIds && sortedFragIds.length === fragCount ? sortedFragIds : new Int32Array(fragCount);
  const memCosts = new Uint32Array(fragCount);
  const maxUint32 = Math.pow(2, 32) - 1;
  for (var i = 0; i < fragCount; i++) {
    // exclude fragments without valid geometry
    if (!fragList.isFragmentActive(i)) {
      continue;
    }

    fragIds[validFrags] = i;
    const geom = fragList.getGeometry(i);
    const geomId = fragList.getGeometryId(i);
    const instCount = geomList.getInstanceCount(geomId);

    // Transparent fragments should never be consolidated and should be sorted to the end after all opaque fragments, so we set their cost to the maximum
    memCosts[i] = fragList.getMaterial(i).transparent ? maxUint32 : instCount * geom.byteSize;

    validFrags++;
  }

  // resize array if we had to skip fragments
  if (validFrags < fragCount) {
    fragIds = new Int32Array(fragIds.buffer, fragIds.byteOffset, validFrags);
  }

  // We do the actual sorting in a worker to avoid blocking the main thread
  let returnResult;

  let { geomIds, materialIds } = getGeomAndMaterialIDs(model);

  return new Promise((resolve) => {
    const jobId = workerJobId++;

    // Worker Callback
    returnResult = (result) => {
      // We might get calls for previous jobs, so we need to check if the result is for the current job
      if (result.data.jobId === jobId) {
        resolve(result.data.fragIds);
      }
    };
    jobContext.worker.addEventListener('message', returnResult);

    const context = {
      operation: "SORT_FRAGMENTS",
      fragIds,
      geomIds,
      memCosts,
      materialIds,
      fragIdToNodeIdx,
      jobId
    };

    jobContext.worker.doOperation(context);
  }).finally(() => {
    jobContext.worker?.removeEventListener('message', returnResult);
  });
}

/**
 * Get the geom and material IDs for the model.
 * @param {RenderModel} model - The model
 * @returns { {geomIds: Uint32Array, materialIds: Uint32Array} } - The geom and material IDs
 */
function getGeomAndMaterialIDs(model) {
  const fl = model.getFragmentList();
  let geomIds, materialIds;

  geomIds = fl.geomids;
  materialIds = fl.materialids;

  return { geomIds, materialIds };
}

/**
 * Get the current consolidation map or create a new one if it does not exist yet or if parameters have changed
 * requirung a recomputation.
 * @param {RenderModel}             model       - The model to consolidate
 * @param {ConsolidationJobContext} jobContext  - Context information for the consolidation job
 * @returns {Promise<ConsolidationMap>}
 */
async function getOrCreateConsolidationMap(model, jobContext) {
  if (!jobContext.consolidationMap || jobContext.forceConsolidationMapRecomputation) {
    const fragList = model.getFragmentList();
    let fragIdToNodeIdx = null; // maps fragment id to bvh node index (only needed for per-tile consolidation)
    let sortedFragIds = new Int32Array(fragList.getCount());

    // create consolidation map
    sortedFragIds = await sortByConsolidationCosts(model, sortedFragIds, fragIdToNodeIdx, jobContext);
    jobContext.stopIfCanceled(CONSOLIDATION_STOP_MARKER.SORTING);
    jobContext.signalProgress(CONSOLIDATION_STOP_MARKER.SORTING.progress);

    jobContext.consolidationMap = await createConsolidationMap(model, sortedFragIds, fragIdToNodeIdx, jobContext);
  }

  return jobContext.consolidationMap;
}

/**
 *  Creates a consolidated representation of a fragments. For each fragment f, there will be a mesh in the result that
 *  contains it - or shares its geometry if was not mergeable with any other fragment.
 *
 *   @param {RenderModel}     model               - The model to consolidate
 *   @param {MaterialManager} materials           - needed to create new material variants for consolidated/instanced meshes
 *   @param {ConsolidationJobContext} jobContext  - Context information for the consolidation job
 *   @returns {Promise<Consolidation>}            - Returns a promise that resolves to a Consolidation object
 */
export async function consolidateFragmentList(model, materials, jobContext) {
  const fragList = model.getFragmentList();

  // If not available yet, create ConsolidationMap that describes the mapping from src fragments
  // into consolidated meshes.
  const consMap = jobContext.consolidationMap = await getOrCreateConsolidationMap(model, jobContext);

  // Create Consolidation
  const consolidation = consMap.buildConsolidation(fragList, materials, model, jobContext.useDeferredConsolidation);

  // Apply instancing to all remaining fragments that were not consolidated yet
  await applyInstancing(model, materials, consolidation, jobContext);

  return consolidation;
}